'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useWallets } from '@morfi/backend-sdk-v2/client';
import { createOnrampClient, type DirectPurchaseCheckout, type DirectPurchaseMethod, type OnrampClient, onrampManifest } from '@morfi/onramp-sdk';
import { DirectPurchaseActionBoundary, useDirectPurchaseContext, useFundingResource, useOnrampSession } from '@morfi/onramp-sdk/react';
import { createMoonPayWebProvider, webFundingMethods } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { toSdkApiBase } from '@/lib/network/sdk-api-base';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { useNetworkConfig } from '@/lib/network/use-network-config';
import { logger } from '@/lib/telemetry/logger';
import { useWalletBalances } from '@/queries/wallet-balance';
import { FundingEntry } from '../funding-entry';
import { fundingTelemetry } from '../shared/funding-telemetry';
interface Props {
  marketAddress: string;
  outcomeIndex: number | null;
  origin: 'market' | 'winner';
  placement?: 'desktop' | 'responsive';
}
export function DirectTokenPurchaseAction(props) {
  const $ = _c(9);
  const active = useMediaQuery(props.placement === "responsive" ? "(max-width: 1023px)" : "(min-width: 1024px)");
  const onrampEnabled = useFeatureGate("onramp_enabled");
  const directPurchaseEnabled = useFeatureGate("direct_token_purchase_enabled");
  const {
    user,
    isAuthenticated
  } = useAuth();
  const config = useNetworkConfig();
  const network = useActiveNetwork();
  let t0;
  if ($[0] !== active || $[1] !== config || $[2] !== directPurchaseEnabled || $[3] !== isAuthenticated || $[4] !== network || $[5] !== onrampEnabled || $[6] !== props || $[7] !== user) {
    t0 = active && onrampEnabled === true && directPurchaseEnabled === true && isAuthenticated && user ? <DirectPurchaseActionBoundary key={`${user.id}:${config.apiUrl}:${network}:${props.marketAddress}`} onFailure={() => logger.warn("onramp.direct_purchase_render_failed", {
      origin: props.origin
    })}><DirectPurchaseActionBody key={`${user.id}:${config.apiUrl}:${network}`} {...props} /></DirectPurchaseActionBoundary> : null;
    $[0] = active;
    $[1] = config;
    $[2] = directPurchaseEnabled;
    $[3] = isAuthenticated;
    $[4] = network;
    $[5] = onrampEnabled;
    $[6] = props;
    $[7] = user;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  return t0;
}
function DirectPurchaseActionBody(t0) {
  const $ = _c(22);
  const {
    marketAddress,
    outcomeIndex,
    origin
  } = t0;
  const onrampEnabled = useFeatureGate("onramp_enabled");
  const directPurchaseEnabled = useFeatureGate("direct_token_purchase_enabled");
  const {
    user,
    isAuthenticated
  } = useAuth();
  const {
    wallets
  } = useWallets();
  const wallet = wallets.find(_temp);
  const network = useActiveNetwork();
  const config = useNetworkConfig();
  const admitted = onrampEnabled === true && directPurchaseEnabled === true && isAuthenticated;
  const balances = useWalletBalances(admitted && wallet ? [wallet.address] : []);
  let t1;
  if ($[0] !== config.apiKey || $[1] !== config.apiUrl || $[2] !== user?.id) {
    t1 = () => createOnrampClient({
      telemetry: fundingTelemetry,
      progressKey: `${user?.id ?? "anonymous"}:${config.apiUrl}`,
      baseUrl: process.env.NODE_ENV === "development" ? window.location.origin : toSdkApiBase(config.apiUrl),
      getAuthHeaders: async () => ({
        "X-API-Key": config.apiKey ?? ""
      })
    });
    $[0] = config.apiKey;
    $[1] = config.apiUrl;
    $[2] = user?.id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const client = useFundingResource(t1);
  let t2;
  if ($[4] !== marketAddress || $[5] !== outcomeIndex) {
    t2 = outcomeIndex === null ? null : {
      marketAddress,
      outcomeIndex
    };
    $[4] = marketAddress;
    $[5] = outcomeIndex;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = wallet?.address ?? null;
  const t4 = !balances.isLoading && !balances.isError && wallet ? balances.perWalletRaw.get(wallet.address)?.toString() ?? null : null;
  let t5;
  if ($[7] !== network) {
    t5 = [...webFundingMethods(network === "mainnet" ? "production" : "sandbox").filter(_temp2), "card"];
    $[7] = network;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== client || $[10] !== directPurchaseEnabled || $[11] !== network || $[12] !== onrampEnabled || $[13] !== t2 || $[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = {
      client,
      purchase: t2,
      wallet: t3,
      cashD6: t4,
      network,
      surface: "web",
      onrampEnabled,
      directPurchaseEnabled,
      deviceMethods: t5
    };
    $[9] = client;
    $[10] = directPurchaseEnabled;
    $[11] = network;
    $[12] = onrampEnabled;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const options = t6;
  let t7;
  if ($[18] !== client || $[19] !== options || $[20] !== origin) {
    t7 = client ? <DirectPurchaseSession options={{
      ...options,
      client
    }} origin={origin} /> : null;
    $[18] = client;
    $[19] = options;
    $[20] = origin;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
function _temp2(method) {
  return method === "apple_pay" || method === "google_pay";
}
function _temp(value) {
  return value.isPrimary && value.archivedAt === null && value.type !== "external";
}
function DirectPurchaseSession(t0) {
  const $ = _c(17);
  const {
    options,
    origin
  } = t0;
  const [entry, setEntry] = useState(null);
  const [open, setOpen] = useState(false);
  const cash = options.cashD6;
  let t1;
  if ($[0] !== cash || $[1] !== options.directPurchaseEnabled || $[2] !== options.onrampEnabled || $[3] !== options.purchase) {
    t1 = options.onrampEnabled === true && options.directPurchaseEnabled === true && options.purchase !== null && cash !== null && /^\d{1,20}$/.test(cash) && BigInt(cash) < BigInt(onrampManifest.direct_token_purchase.settings.buy_action_threshold_usdc_d6);
    $[0] = cash;
    $[1] = options.directPurchaseEnabled;
    $[2] = options.onrampEnabled;
    $[3] = options.purchase;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = {
      enabled: t1
    };
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const session = useOnrampSession(options.client, t2);
  let t3;
  if ($[7] !== options || $[8] !== origin || $[9] !== session.data) {
    t3 = session.data ? <DirectPurchaseProvider key={session.data.sessionToken} options={options} origin={origin} sessionToken={session.data.sessionToken} sessionProofId={session.data.sessionProofId} onChoose={purchase => {
      setEntry(purchase);
      setOpen(true);
    }} /> : null;
    $[7] = options;
    $[8] = origin;
    $[9] = session.data;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== entry || $[12] !== open) {
    t4 = entry ? <FundingEntry open={open} directPurchase={entry} onClose={() => setOpen(false)} /> : null;
    $[11] = entry;
    $[12] = open;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
interface ProviderProps {
  options: Omit<Parameters<typeof useDirectPurchaseContext>[0], 'provider' | 'sessionProofId'>;
  origin: 'market' | 'winner';
  sessionToken: string;
  sessionProofId: string;
  onChoose(entry: DirectPurchaseCheckout): void;
}
function DirectPurchaseProvider(props) {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.sessionToken) {
    t0 = () => createMoonPayWebProvider({
      sessionToken: props.sessionToken,
      theme: {
        appearance: "dark"
      }
    });
    $[0] = props.sessionToken;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const provider = useFundingResource(t0);
  let t1;
  if ($[2] !== props || $[3] !== provider) {
    t1 = provider ? <DirectPurchaseReady {...props} provider={provider} /> : null;
    $[2] = props;
    $[3] = provider;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function DirectPurchaseReady(t0) {
  const $ = _c(21);
  const {
    options,
    origin,
    sessionProofId,
    provider,
    onChoose
  } = t0;
  let t1;
  if ($[0] !== options || $[1] !== provider || $[2] !== sessionProofId) {
    t1 = {
      ...options,
      provider,
      sessionProofId
    };
    $[0] = options;
    $[1] = provider;
    $[2] = sessionProofId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const discovery = useDirectPurchaseContext(t1);
  const t = useTranslations("onramp");
  let t2;
  let t3;
  if ($[4] !== discovery.isError || $[5] !== origin) {
    t2 = () => {
      if (discovery.isError) {
        logger.warn("onramp.direct_purchase_discovery_failed", {
          origin
        });
      }
    };
    t3 = [discovery.isError, origin];
    $[4] = discovery.isError;
    $[5] = origin;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== discovery.context?.purchase || $[9] !== discovery.methods || $[10] !== onChoose || $[11] !== origin) {
    t4 = function choose(method) {
      const binding = discovery.context?.purchase;
      if (!binding || !discovery.methods.includes(method)) {
        return;
      }
      onChoose({
        purchase: {
          marketAddress: binding.marketAddress,
          outcomeIndex: binding.outcomeIndex
        },
        binding,
        preferredMethod: method,
        origin
      });
    };
    $[8] = discovery.context?.purchase;
    $[9] = discovery.methods;
    $[10] = onChoose;
    $[11] = origin;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const choose = t4;
  let t5;
  if ($[13] !== discovery.methods) {
    t5 = discovery.methods.filter(_temp3);
    $[13] = discovery.methods;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const walletsAvailable = t5;
  const methods = walletsAvailable.length ? walletsAvailable : discovery.methods;
  let t6;
  if ($[15] !== choose || $[16] !== methods || $[17] !== t) {
    t6 = methods.length > 0 && <div className="flex flex-col gap-2 motion-safe:animate-in motion-safe:fade-in">{methods.map(method_1 => <AnimatedButton key={method_1} variant="secondary" onClick={() => choose(method_1)}>{method_1 === "card" ? t("direct_purchase_card") : t("direct_purchase_buy", {
          method: t(method_1)
        })}</AnimatedButton>)}</div>;
    $[15] = choose;
    $[16] = methods;
    $[17] = t;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t6) {
    t7 = <>{t6}</>;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
function _temp3(method_0) {
  return method_0 !== "card";
}